<template>
  <div class="workflow-output-page">
    <!-- Header -->
    <div class="output-header">
      <div class="header-left">
        <button class="back-btn" @click="goBack">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
        </button>
        <h1 class="page-title">Automation Activity</h1>
      </div>
      <div class="header-actions">
        <button class="btn btn-refresh" @click="loadOutputs" :disabled="isLoading">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"></polyline><polyline points="1 20 1 14 7 14"></polyline><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>
          Refresh
        </button>
      </div>
    </div>

    <ErrorDisplay />

    <!-- Loading Overlay -->
    <CommonOverlay :show="isLoading" title="Loading" message="Fetching execution history..." />

    <!-- Output Panel -->
    <div class="output-panel">
      <div class="panel-card">
        <div class="panel-header">
          <h5 class="section-title">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="section-icon"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline></svg>
            Automation Runs
          </h5>
          <span class="run-count" v-if="outputs.length > 0">
            Showing {{ filteredOutputs.length }} of {{ outputs.length }}
          </span>
        </div>

        <!-- Filter Bar (only when there's something to filter) -->
        <div class="filter-bar" v-if="outputs.length > 0">
          <div class="search-box">
            <svg class="search-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
            <input
              type="text"
              class="search-input"
              placeholder="Search by patient name, action, or details..."
              v-model="searchQuery"
            />
            <button v-if="searchQuery" class="clear-btn" @click="searchQuery = ''" title="Clear">&times;</button>
          </div>
          <div class="status-filter">
            <label class="filter-label">Status:</label>
            <select class="filter-select" v-model="statusFilter">
              <option value="all">All</option>
              <option value="success">Success only</option>
              <option value="failed">Failed only</option>
            </select>
          </div>
        </div>

        <!-- Empty State (no runs at all) -->
        <div class="empty-state" v-if="outputs.length === 0 && !isLoading">
          <div class="empty-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline></svg>
          </div>
          <h4>No Automations Have Run Yet</h4>
          <p>Once this workflow's trigger fires, you'll see each run here with details about what happened.</p>
        </div>

        <!-- Empty State (filtered to nothing) -->
        <div class="empty-state" v-if="outputs.length > 0 && filteredOutputs.length === 0">
          <div class="empty-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
          </div>
          <h4>No Matches</h4>
          <p>No runs match your search or filter. Try clearing them to see all runs.</p>
        </div>

        <!-- Output Table -->
        <div v-if="filteredOutputs.length > 0" class="output-table-container">
          <!-- Table Header -->
          <div class="output-table-header">
            <div class="col-status">Status</div>
            <div class="col-action">Action</div>
            <div class="col-patient">Patient</div>
            <div class="col-result">Details</div>
            <div class="col-timestamp">When</div>
            <div class="col-actions"></div>
          </div>

          <!-- Output Items -->
          <div
            class="output-item"
            v-for="(output, index) in filteredOutputs"
            :key="output.WorkflowOutputID || index"
            :class="{ 'success': output.Successful, 'failed': !output.Successful }"
          >
            <!-- Status Column -->
            <div class="col-status">
              <span class="status-badge" :class="output.Successful ? 'status-success' : 'status-failed'">
                <svg v-if="output.Successful" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                {{ output.Successful ? 'Success' : 'Failed' }}
              </span>
            </div>

            <!-- Action Column -->
            <div class="col-action">
              <span class="action-badge" :class="getActionInfo(output).className">
                <span class="action-emoji">{{ getActionInfo(output).emoji }}</span>
                <span class="action-label">{{ getActionInfo(output).label }}</span>
              </span>
            </div>

            <!-- Patient Column -->
            <div class="col-patient">
              <template v-if="output.PatNum">
                <router-link
                  :to="{ name: 'patientprofile', params: { patientID: patientRouteId(output) } }"
                  class="patient-link"
                  :title="`Open patient profile (#${output.PatNum})`"
                >
                  {{ getPatientName(output) }}
                </router-link>
                <span class="patient-id">#{{ output.PatNum }}</span>
              </template>
              <span v-else class="no-patient">—</span>
            </div>

            <!-- Details Column -->
            <div class="col-result">
              <div class="result-preview" v-html="truncateResult(output.Result)"></div>
            </div>

            <!-- When Column -->
            <div class="col-timestamp" :title="formatAbsoluteTime(output.LastRanOn)">
              {{ formatRelativeTime(output.LastRanOn) }}
            </div>

            <!-- Actions Column -->
            <div class="col-actions">
              <button class="action-btn" @click="showResultDetails(output)" title="View Details">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Result Details Modal -->
    <div class="modal-overlay" v-if="showDetailsModal" @click="closeDetailsModal">
      <div class="modal-content modal-large" @click.stop>
        <div class="modal-header">
          <h3>Run Details</h3>
          <button class="close-btn" @click="closeDetailsModal">&times;</button>
        </div>
        <div class="modal-body">
          <div class="detail-section">
            <label>Status</label>
            <div class="detail-value">
              <span class="status-badge" :class="selectedOutput?.Successful ? 'status-success' : 'status-failed'">
                {{ selectedOutput?.Successful ? 'Success' : 'Failed' }}
              </span>
            </div>
          </div>
          <div class="detail-section" v-if="selectedOutput">
            <label>Action</label>
            <div class="detail-value">
              <span class="action-badge" :class="getActionInfo(selectedOutput).className">
                <span class="action-emoji">{{ getActionInfo(selectedOutput).emoji }}</span>
                <span class="action-label">{{ getActionInfo(selectedOutput).label }}</span>
              </span>
            </div>
          </div>
          <div class="detail-section" v-if="selectedOutput && selectedOutput.PatNum">
            <label>Patient</label>
            <div class="detail-value">
              <router-link
                :to="{ name: 'patientprofile', params: { patientID: patientRouteId(selectedOutput) } }"
                class="patient-link"
              >
                {{ getPatientName(selectedOutput) }}
              </router-link>
              <span class="patient-id">#{{ selectedOutput.PatNum }}</span>
            </div>
          </div>
          <div class="detail-section">
            <label>Ran On</label>
            <div class="detail-value">
              {{ formatAbsoluteTime(selectedOutput?.LastRanOn) }}
              <span class="muted">({{ formatRelativeTime(selectedOutput?.LastRanOn) }})</span>
            </div>
          </div>
          <div class="detail-section">
            <label>Details</label>
            <div class="detail-value result-full" v-html="selectedOutput?.Result"></div>
          </div>
          <div class="detail-section" v-if="selectedOutput?.WorkflowRunID">
            <label>Run ID <span class="muted">(for support)</span></label>
            <div class="detail-value code-value">
              {{ selectedOutput.WorkflowRunID }}
              <button class="copy-btn-inline" @click="copyRunId(selectedOutput.WorkflowRunID)">Copy</button>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-primary" @click="closeDetailsModal">Close</button>
        </div>
      </div>
    </div>

    <!-- Copy Success Toast -->
    <div class="toast" :class="{ 'show': showCopyToast }">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
      Copied to clipboard!
    </div>
  </div>
</template>

<script>
/* eslint-disable */

import { mapState, mapActions } from 'vuex';
import moment from 'moment';
import ErrorDisplay from "@/components/ErrorDisplay/ErrorDisplay";
import CommonOverlay from "@/components/CommonOverlay";
import mixin from '../../mixin.js';

// Best-effort patient-name extraction from the legacy Result text.
// Result formats observed:
//   "SMS status for Shah, Test82: <b>Sent</b> (Procedure: ...)"
//   "Email status for Shah, Test82: queued"
//   "New patient email status for Shah, Test82: queued"
//   "Cannot send new patient SMS to Shah, Test82 — wireless phone is missing"
//   "Error sending SMS to Shah, Test82: ..."
//   "Skipping Email send to Patient Name because ..."
const PATIENT_NAME_PATTERNS = [
  /(?:SMS|Email|email) status for ([^:]+?):/i,
  /(?:send to|SMS to|email to)\s+(.+?)\s*(?:[—\-:(]|because|$)/i,
  /sending (?:SMS|Email|email) to\s+(.+?)\s*[:—\-]/i,
];

function extractPatientNameFromResult(result) {
  if (!result) return null;
  const plain = String(result).replace(/<[^>]*>/g, '').trim();
  for (const re of PATIENT_NAME_PATTERNS) {
    const m = plain.match(re);
    if (m && m[1]) {
      return m[1].trim();
    }
  }
  return null;
}

function deriveActionInfo(output) {
  const channel = (output.Channel || '').toUpperCase();
  if (channel === 'SMS') return { emoji: '💬', label: 'SMS', className: 'action-sms' };
  if (channel === 'EMAIL') return { emoji: '📧', label: 'Email', className: 'action-email' };
  if (channel === 'FORM') return { emoji: '📝', label: 'Form', className: 'action-form' };

  // Fallback: parse from Result text for older rows that pre-date the Channel column
  const text = String(output.Result || '').replace(/<[^>]*>/g, '');
  if (/SMS status|sending SMS|new patient SMS/i.test(text)) return { emoji: '💬', label: 'SMS', className: 'action-sms' };
  if (/email status|sending email|new patient email/i.test(text)) return { emoji: '📧', label: 'Email', className: 'action-email' };
  if (/^Task /i.test(text) || /task .* assigned to/i.test(text)) return { emoji: '✅', label: 'Task', className: 'action-task' };
  if (/form/i.test(text)) return { emoji: '📝', label: 'Form', className: 'action-form' };
  return { emoji: '⚙️', label: 'Other', className: 'action-other' };
}

export default {
  name: 'WorkflowOutput2',
  components: { ErrorDisplay, CommonOverlay },
  mixins: [mixin],
  props: {
    workflowID: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      isLoading: false,
      outputs: [],
      showDetailsModal: false,
      selectedOutput: null,
      showCopyToast: false,
      searchQuery: '',
      statusFilter: 'all'
    };
  },

  computed: {
    ...mapState({
      siteID: state => state.customer.siteID
    }),

    filteredOutputs() {
      const q = this.searchQuery.trim().toLowerCase();
      return this.outputs.filter(o => {
        // Status filter
        if (this.statusFilter === 'success' && !o.Successful) return false;
        if (this.statusFilter === 'failed' && o.Successful) return false;

        // Search filter — match against Result text (which contains patient name + procedure),
        // PatNum, and the derived action label
        if (q) {
          const haystack = [
            String(o.Result || '').replace(/<[^>]*>/g, ''),
            String(o.PatNum || ''),
            deriveActionInfo(o).label
          ].join(' ').toLowerCase();
          if (!haystack.includes(q)) return false;
        }

        return true;
      });
    }
  },

  created() {
    this.loadOutputs();
  },

  methods: {
    ...mapActions('workflow', ['getWorkflowOutput']),

    async loadOutputs() {
      if (!this.workflowID) return;

      this.isLoading = true;
      try {
        const response = await this.getWorkflowOutput({
          WorkflowID: this.workflowID,
          siteID: this.siteID
        });
        this.outputs = response || [];
      } catch (err) {
        console.error('Error loading workflow outputs:', err);
        this.outputs = [];
      } finally {
        this.isLoading = false;
      }
    },

    goBack() {
      this.$router.push({ name: 'Workflow2List' });
    },

    truncateResult(result) {
      if (!result) return '<span class="no-result">No details</span>';
      const text = result.replace(/<[^>]*>/g, ''); // Strip HTML for preview
      if (text.length <= 100) return text;
      return text.substring(0, 100) + '...';
    },

    getActionInfo(output) {
      return deriveActionInfo(output);
    },

    getPatientName(output) {
      const parsed = extractPatientNameFromResult(output && output.Result);
      if (parsed) return parsed;
      return `Patient #${output.PatNum}`;
    },

    // Patient profile route uses `${siteID}-${PatNum}` as the patientID param.
    // Prefer the per-row SiteID (added by our schema migration); fall back to the
    // current site context for legacy rows that pre-date the new column.
    patientRouteId(output) {
      const sid = (output && output.SiteID) || this.siteID;
      return `${sid}-${output.PatNum}`;
    },

    formatRelativeTime(dt) {
      if (!dt) return '';
      const m = moment(dt);
      if (!m.isValid()) return '';
      const diffMin = moment().diff(m, 'minutes');
      if (diffMin < 1) return 'just now';
      if (diffMin < 60) return `${diffMin}m ago`;
      const diffHr = moment().diff(m, 'hours');
      if (diffHr < 24) return `${diffHr}h ago`;
      const diffDay = moment().diff(m, 'days');
      if (diffDay === 1) return 'yesterday';
      if (diffDay < 7) return `${diffDay}d ago`;
      return m.format('MMM D');
    },

    formatAbsoluteTime(dt) {
      if (!dt) return '';
      const m = moment(dt);
      if (!m.isValid()) return '';
      return m.format('MMM D, YYYY [at] h:mm A');
    },

    showResultDetails(output) {
      this.selectedOutput = output;
      this.showDetailsModal = true;
    },

    closeDetailsModal() {
      this.showDetailsModal = false;
      this.selectedOutput = null;
    },

    async copyRunId(runId) {
      if (!runId) return;

      try {
        await navigator.clipboard.writeText(runId);
        this.showCopyToast = true;
        setTimeout(() => {
          this.showCopyToast = false;
        }, 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  }
};
</script>

<style scoped lang="scss">
/* Workflow Output Styles */

.workflow-output-page {
  min-height: 100vh;
  background-color: #f9fafb;
}

/* Header */
.output-header {
  background: linear-gradient(to right, #6366f1, #8b5cf6);
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;

  .page-title {
    color: white;
    font-size: 20px;
    font-weight: 600;
    margin: 0;
  }
}

.back-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 8px;
  color: white;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
}

.header-actions {
  display: flex;
  gap: 12px;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.btn-refresh {
  background: white;
  color: #6366f1;

  &:hover:not(:disabled) {
    background: #f9fafb;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.btn-primary {
  background: #6366f1;
  color: white;

  &:hover {
    background: #4f46e5;
  }
}

/* Panel */
.output-panel {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
}

.panel-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.panel-header {
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin: 0;

  .section-icon {
    color: #6366f1;
  }
}

.run-count {
  font-size: 13px;
  color: #6b7280;
  background: #f3f4f6;
  padding: 4px 10px;
  border-radius: 12px;
}

/* Filter Bar */
.filter-bar {
  display: flex;
  gap: 12px;
  padding: 12px 20px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  align-items: center;
  flex-wrap: wrap;
}

.search-box {
  flex: 1;
  min-width: 240px;
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0 10px;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:focus-within {
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
  }

  .search-icon {
    color: #9ca3af;
    margin-right: 8px;
    flex-shrink: 0;
  }

  .search-input {
    flex: 1;
    border: none;
    outline: none;
    padding: 8px 0;
    font-size: 14px;
    background: transparent;
    color: #374151;

    &::placeholder {
      color: #9ca3af;
    }
  }

  .clear-btn {
    background: none;
    border: none;
    color: #9ca3af;
    font-size: 20px;
    line-height: 1;
    cursor: pointer;
    padding: 0 4px;

    &:hover {
      color: #374151;
    }
  }
}

.status-filter {
  display: flex;
  align-items: center;
  gap: 8px;

  .filter-label {
    font-size: 13px;
    font-weight: 500;
    color: #6b7280;
    margin: 0;
  }

  .filter-select {
    border: 1px solid #d1d5db;
    border-radius: 8px;
    padding: 7px 28px 7px 10px;
    font-size: 14px;
    background: white;
    color: #374151;
    cursor: pointer;
    outline: none;
    transition: border-color 0.15s, box-shadow 0.15s;

    &:focus {
      border-color: #6366f1;
      box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
    }
  }
}

/* Empty State */
.empty-state {
  padding: 60px 20px;
  text-align: center;

  .empty-icon {
    color: #9ca3af;
    margin-bottom: 16px;
  }

  h4 {
    font-size: 18px;
    color: #374151;
    margin: 0 0 8px;
  }

  p {
    color: #6b7280;
    margin: 0 auto;
    max-width: 460px;
  }
}

/* Table */
.output-table-container {
  overflow-x: auto;
}

.output-table-header {
  display: grid;
  grid-template-columns: 100px 110px 200px 1fr 110px 60px;
  padding: 12px 20px;
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.output-item {
  display: grid;
  grid-template-columns: 100px 110px 200px 1fr 110px 60px;
  padding: 16px 20px;
  border-bottom: 1px solid #f3f4f6;
  align-items: center;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f9fafb;
  }

  &:last-child {
    border-bottom: none;
  }

  &.success {
    border-left: 3px solid #22c55e;
  }

  &.failed {
    border-left: 3px solid #ef4444;
  }
}

/* Status badge */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;

  &.status-success {
    background: #dcfce7;
    color: #16a34a;
  }

  &.status-failed {
    background: #fee2e2;
    color: #dc2626;
  }
}

/* Action badge */
.action-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;

  .action-emoji {
    font-size: 13px;
    line-height: 1;
  }

  &.action-sms {
    background: #dbeafe;
    color: #1d4ed8;
  }
  &.action-email {
    background: #ede9fe;
    color: #6d28d9;
  }
  &.action-task {
    background: #fef3c7;
    color: #b45309;
  }
  &.action-form {
    background: #d1fae5;
    color: #047857;
  }
  &.action-other {
    background: #f3f4f6;
    color: #4b5563;
  }
}

/* Patient column */
.col-patient {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  .patient-link {
    font-size: 14px;
    font-weight: 500;
    color: #4f46e5;
    text-decoration: none;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &:hover {
      text-decoration: underline;
      color: #4338ca;
    }
  }

  .patient-id {
    font-size: 11px;
    color: #9ca3af;
    font-family: monospace;
  }

  .no-patient {
    color: #9ca3af;
    font-size: 14px;
  }
}

/* Result/Details column */
.col-result {
  min-width: 0;

  .result-preview {
    font-size: 13px;
    color: #6b7280;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .no-result {
    color: #9ca3af;
    font-style: italic;
  }
}

.col-timestamp {
  font-size: 13px;
  color: #6b7280;
  cursor: help;
}

.col-actions {
  display: flex;
  justify-content: center;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
    color: #6366f1;
  }
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  animation: slideIn 0.2s ease;

  &.modal-large {
    max-width: 700px;
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;

  h3 {
    font-size: 16px;
    font-weight: 600;
    margin: 0;
  }

  .close-btn {
    background: none;
    border: none;
    font-size: 24px;
    color: #9ca3af;
    cursor: pointer;

    &:hover {
      color: #6b7280;
    }
  }
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.detail-section {
  margin-bottom: 16px;

  label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 6px;

    .muted {
      font-weight: 400;
      text-transform: none;
      letter-spacing: 0;
      color: #9ca3af;
    }
  }

  .detail-value {
    font-size: 14px;
    color: #374151;

    .muted {
      color: #9ca3af;
      margin-left: 6px;
      font-size: 13px;
    }

    &.code-value {
      font-family: monospace;
      font-size: 12px;
      background: #f9fafb;
      padding: 8px 12px;
      border-radius: 6px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    &.result-full {
      background: #f9fafb;
      padding: 12px;
      border-radius: 8px;
      max-height: 300px;
      overflow-y: auto;
      font-size: 13px;
      line-height: 1.6;
    }

    .patient-link {
      color: #4f46e5;
      text-decoration: none;
      font-weight: 500;

      &:hover {
        text-decoration: underline;
      }
    }

    .patient-id {
      color: #9ca3af;
      font-family: monospace;
      font-size: 12px;
      margin-left: 8px;
    }
  }
}

.copy-btn-inline {
  padding: 4px 12px;
  background: #6366f1;
  border: none;
  border-radius: 4px;
  color: white;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background: #4f46e5;
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  padding: 16px 20px;
  border-top: 1px solid #e5e7eb;
}

/* Toast */
.toast {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%) translateY(100px);
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: #374151;
  color: white;
  border-radius: 8px;
  font-size: 14px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  opacity: 0;
  transition: all 0.3s ease;

  &.show {
    transform: translateX(-50%) translateY(0);
    opacity: 1;
  }
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .output-table-header {
    display: none;
  }

  .output-item {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding: 16px;
  }

  .filter-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .status-filter {
    justify-content: space-between;
  }
}
</style>
